import {
    ArrowBack as ArrowBackIcon,
    ArrowBackIosNewOutlined as ArrowBackIosNewOutlinedIcon,
    OfflineBoltOutlined as OfflineBoltOutlinedIcon,
    PlaylistAddOutlined as PlaylistAddOutlinedIcon,
    PlaylistRemoveOutlined as PlaylistRemoveOutlinedIcon,
} from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import is from '@sindresorhus/is';
import { Fragment, useContext } from 'react';
import { matchRoutes } from 'react-router';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import { useUpdateUserAmbitions } from '../../../../../shared/source/query-hooks/use-ambition';
import { SecondaryActionButton } from '../../../design/components/button/SecondaryAction';
import { InlineLoadingSpinner } from '../../../design/components/LoadingSpinner';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { SkillChipWithDrawer } from '../../common/skills/SkillChipWithDrawer';
import { useSnackbars } from '../../error-handling/SnackBars';
import { useRelatedSkillsQuery } from '../../query-hooks/skill-details';
import { route as jobsRoute } from '../job-list/constants';
import { route as learningOpportunitiesRoute } from '../learning-opportunities/constants';

import type { UserAmbitionUpdatePayload } from '../../../../../shared/source/data-contract/ambition';
import type { SkillShort } from '../../../../../shared/source/data-contract/skill';
import type { RelatedSkills } from '../../../../../shared/source/data-contract/skill-details';
import type { GridProps } from '@mui/material/Grid';
import type { ComponentProps, FunctionComponent, PropsWithChildren, ReactElement, ReactNode } from 'react';

export const SkillsDetailsRow: FunctionComponent<PropsWithChildren<GridProps>> = ({ children }) => (
    <Grid flexGrow={1} size="grow" sx={{ width: '100%' }}>
        {children}
    </Grid>
);

export const RelatedSkill: FunctionComponent<{ readonly relatedSkill: RelatedSkills[number] }> = ({ relatedSkill }) => (
    <Box sx={{ boxShadow: 'none', fontSize: 16, fontWeight: 600, maxWidth: '100%' }}>
        <SkillChipWithDrawer label={relatedSkill.title} skillId={relatedSkill.id} type={relatedSkill.skillType} />
    </Box>
);

export const CardWithHeadline: FunctionComponent<
    PropsWithChildren<ComponentProps<typeof Card> & { readonly headline: string }>
> = ({ children, headline, ...rest }) => {
    const { palette } = useTheme();

    return (
        <Card
            sx={{
                ...rest.sx,
                border: `solid 1px ${palette.cardborder.main}`,
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
            }}
        >
            <CardContent>
                <Typography component="h5" variant="h5">
                    {headline}
                </Typography>
            </CardContent>
            <Divider />
            <Grid flexGrow={1}>{children}</Grid>
        </Card>
    );
};

export const BaseSkillHeadline: FunctionComponent<{
    readonly doesSkillExist: boolean;
    readonly handleBack?: () => void;
    readonly handleSkillAction: (isLanguage: boolean) => void;
    readonly isAmbition?: boolean;
    readonly showBackIcon: boolean;
    readonly skill: SkillShort;
    readonly userId: string;
    // eslint-disable-next-line complexity
}> = ({ doesSkillExist, handleBack, handleSkillAction, isAmbition, showBackIcon, skill, userId }) => {
    const {
        applicationState: { location, skillDrawerStates },
        translate,
    } = useContext(ApplicationContext);
    const { id: skillId, skillType, title } = skill;
    const { palette } = useTheme();
    const { sendTrackingEvent } = useApplicationFetchers();
    const paths = [
        {
            path: learningOpportunitiesRoute.patterns.createNewLearningOpportunity,
        },
        {
            path: learningOpportunitiesRoute.patterns.learningOpportunityEdit,
        },
        {
            path: jobsRoute.patterns.job.create,
        },
        {
            path: jobsRoute.patterns.job.edit,
        },
    ];

    const matches = matchRoutes(paths, location);

    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();

    const updateAmbitionMutation = useUpdateUserAmbitions(userId);

    const showAddToRequiredSkillsButton = is.nonEmptyArray(matches) && !is.nullOrUndefined(skillDrawerStates);

    const isSkillAlreadyRequired = skillDrawerStates?.excludedSkills.some((excludedSkill) => excludedSkill === skillId);

    const onAddToAmbitionsClick = (): void => {
        if (is.nonEmptyString(skillId)) {
            const payload: UserAmbitionUpdatePayload = {
                add: [{ id: skillId }],
                remove: [],
            };
            sendTrackingEvent({
                name: 'ambition-added',
                payload: {
                    entityType: 'skill',
                    entryPoint: 'skill-drawer',
                    number: 1,
                },
            });
            updateAmbitionMutation.mutate(payload, {
                onError: () => {
                    showErrorSnackbar('errors.component.job.update-ambitions');
                },
                onSuccess: () => {
                    showSuccessSnackbar('success.ambitions.updated');
                },
            });
        }
    };

    const onDeleteFromAmbitionsClick = (): void => {
        if (is.nonEmptyString(skillId)) {
            const payload: UserAmbitionUpdatePayload = {
                add: [],
                remove: [skillId],
            };
            updateAmbitionMutation.mutate(payload, {
                onError: () => {
                    showErrorSnackbar('errors.component.job.update-ambitions');
                },
                onSuccess: () => {
                    showSuccessSnackbar('success.ambitions.updated');
                },
            });
        }
    };

    return (
        <SkillsDetailsRow>
            <Card component={Grid} container={true} flexDirection="column" sx={{ height: 1, p: 2 }}>
                <Grid alignItems="center" color={palette.hardskill.main} container={true} flexGrow={1} gap={1}>
                    {showBackIcon && (
                        <IconButton aria-label="back button" size="small" sx={{ p: 0 }} onClick={handleBack}>
                            <ArrowBackIcon color="secondary" />
                        </IconButton>
                    )}
                    <OfflineBoltOutlinedIcon />
                    <Box pt="2px">{translate('pages.skill-details.headlines.skill')}</Box>
                </Grid>
                <Typography component="h4" sx={{ mt: '30px' }} variant="h4">
                    {title}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'flex-end', mt: 5 }}>
                    {showAddToRequiredSkillsButton ? (
                        <Button
                            color="secondary"
                            disabled={isSkillAlreadyRequired}
                            startIcon={<PlaylistAddOutlinedIcon />}
                            sx={{ fontSize: 14 }}
                            variant="text"
                            onClick={() => skillDrawerStates.onAddSkill(skill)}
                        >
                            {translate('pages.skill-details.buttons.add-to-required-skills')}
                        </Button>
                    ) : (
                        <Fragment>
                            {isAmbition === true ? (
                                <SecondaryActionButton
                                    aria-label="Remove from ambitions button"
                                    isDisabled={
                                        !is.nonEmptyString(skillId) ||
                                        !is.nonEmptyString(userId) ||
                                        updateAmbitionMutation.isPending
                                    }
                                    label={translate('components.remove-from-ambitions-button')}
                                    startIcon={<RemoveCircleOutlineIcon />}
                                    onClick={onDeleteFromAmbitionsClick}
                                />
                            ) : (
                                <SecondaryActionButton
                                    aria-label="Add to ambitions button"
                                    isDisabled={
                                        !is.nonEmptyString(skillId) ||
                                        !is.nonEmptyString(userId) ||
                                        updateAmbitionMutation.isPending
                                    }
                                    label={translate('components.add-to-ambitions-button')}
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={onAddToAmbitionsClick}
                                />
                            )}
                            <Button
                                color="secondary"
                                startIcon={
                                    doesSkillExist ? <PlaylistRemoveOutlinedIcon /> : <PlaylistAddOutlinedIcon />
                                }
                                sx={{ fontSize: 14 }}
                                variant="text"
                                onClick={() => handleSkillAction(skillType === 'language')}
                            >
                                {doesSkillExist
                                    ? translate('pages.skill-details.buttons.remove-from-skill-profile')
                                    : translate('pages.skill-details.buttons.add-to-skill-profile')}
                            </Button>
                        </Fragment>
                    )}
                </Box>
            </Card>
        </SkillsDetailsRow>
    );
};

export const BaseSkillDescription: FunctionComponent<{
    readonly collapsed: boolean;
    readonly content: string | readonly ReactElement<any>[];
    readonly isCollapsable: boolean;
    readonly onCollapseClick: () => void;
}> = ({ collapsed, content, isCollapsable, onCollapseClick }) => {
    const { translate } = useContext(ApplicationContext);

    return (
        <SkillsDetailsRow>
            <CardWithHeadline headline={translate('pages.skill-details.headlines.skill-description')}>
                <CardContent component={Grid} container={true} flexDirection="column">
                    <Typography>{content}</Typography>
                    {isCollapsable && (
                        <Grid component="span" container={true} flexDirection="row-reverse" sx={{ mt: 1 }}>
                            <Button
                                color="secondary"
                                startIcon={
                                    <ArrowBackIosNewOutlinedIcon
                                        sx={{
                                            fontSize: '14px !important',
                                            transform: collapsed ? 'rotate(-90Deg)' : 'rotate(90Deg)',
                                        }}
                                    />
                                }
                                variant="text"
                                onClick={onCollapseClick}
                            >
                                {collapsed
                                    ? translate('pages.skill-details.buttons.expand')
                                    : translate('pages.skill-details.buttons.collapse')}
                            </Button>
                        </Grid>
                    )}
                </CardContent>
            </CardWithHeadline>
        </SkillsDetailsRow>
    );
};

export const JobPostingsRow: FunctionComponent<{
    readonly jobPostingsNode: ReactNode;
    readonly totalJobPostings: number;
}> = ({ jobPostingsNode, totalJobPostings }) => {
    const { translate } = useContext(ApplicationContext);

    return (
        <SkillsDetailsRow>
            <CardWithHeadline
                headline={translate('pages.skill-details.headlines.job-postings', { total: totalJobPostings })}
            >
                <div style={{ height: '70vh' }}>{jobPostingsNode}</div>
            </CardWithHeadline>
        </SkillsDetailsRow>
    );
};

export const RelatedSkillsRow: FunctionComponent<{ readonly skillId: string | null }> = ({ skillId }) => {
    const { translate } = useContext(ApplicationContext);

    const { data: relatedSkills, isPending } = useRelatedSkillsQuery(skillId);

    return (
        <SkillsDetailsRow>
            <CardWithHeadline headline={translate('pages.skill-details.headlines.related-skills')}>
                <Box display="flex" flexWrap="wrap" gap={0.5} padding={2}>
                    {isPending ? (
                        <InlineLoadingSpinner />
                    ) : (
                        relatedSkills?.map((relatedSkill) => (
                            <Fragment key={relatedSkill.id}>
                                <RelatedSkill relatedSkill={relatedSkill} />
                            </Fragment>
                        ))
                    )}
                </Box>
            </CardWithHeadline>
        </SkillsDetailsRow>
    );
};
